import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useContext, useEffect } from "react";
import { Button, Divider, Flex, Modal, Space, Table } from "antd";
import { LeftOutlined, ProfileOutlined } from "@ant-design/icons";
import { PATH_INFO } from "../../../util/helper";
import { DeviceContext, DeviceResponseType, UpdateDeviceContext } from "../../../../context/deviceContext";
import dayjs from "dayjs";
import { CompanyContext, UpdateCompanyContext } from "../../../../context/companyContext";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";

const DEFAULT_PAGE_SIZE = 6;

export function CompanyDeviceList() {
  const { deviceList, devicePagination } = useContext(DeviceContext);
  const { refreshDeviceList, deleteDevice } = useContext(UpdateDeviceContext);
  const { company } = useContext(CompanyContext);
  const { refreshCompany } = useContext(UpdateCompanyContext);
  const { companyId } = useParams();
  const navigate = useNavigate();

  const currentPage = devicePagination.page || 1;
  const currentPageSize = devicePagination.pageSize || DEFAULT_PAGE_SIZE;

  useEffect(() => {
    if (!companyId) return;
    refreshDeviceList(companyId, { page: 1 });
    refreshCompany(companyId);
  }, [companyId, refreshCompany, refreshDeviceList]);

  const onClickManage = useCallback((id: string) => {
    if (!companyId) return;
    const result = PATH_INFO.COMPANY_DEVICE_MANAGE.PATH.replace(':companyId', companyId);
    const _result = result.replace(':deviceId', id);

    navigate(_result);
  }, [companyId, navigate]);

  const onClickDelete = useCallback(async (id: string) => {
    const res = await deleteDevice(id);
    if (res) {
      if (companyId) {
        const pageSize = DEFAULT_PAGE_SIZE;
        const totalAfterDelete = Math.max(devicePagination.total - 1, 0);
        const maxPage = Math.max(1, Math.ceil(totalAfterDelete / pageSize));
        const targetPage = Math.min(devicePagination.page || 1, maxPage);
        await refreshDeviceList(companyId, { page: targetPage });
      }
      Modal.success({ content: 'デバイスの削除が完了しました！', centered: true });
    } else {
      Modal.error({ content: '処理中にエラーが発生しました。', centered: true });
    }
  }, [deleteDevice, refreshDeviceList, companyId, devicePagination.page, devicePagination.total]);

  const handleTableChange = useCallback((pagination: TablePaginationConfig) => {
    if (!companyId) return;
    const nextPage = pagination.current ?? currentPage;

    if (nextPage === currentPage) {
      return;
    }

    refreshDeviceList(companyId, { page: nextPage });
  }, [companyId, currentPage, refreshDeviceList]);

  const columns: ColumnsType<DeviceResponseType> = [
    {
      title: '初期設定',
      dataIndex: 'initial_settings',
      key: 'initial_settings',
      render: (_: any, record: DeviceResponseType) => (
        // record === true ? (<CheckCircleOutlined className="text-green-500 text-xl" />) : (<CloseCircleOutlined className="text-red-500 text-xl" />)
        <></> //未使用のため表示しない
      ),
      className: "text-center",
      width: 90,
    },
    {
      title: '拠点',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'デバイス名',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'API key',
      dataIndex: 'api_key',
      key: 'api_key',
      width: 200,
    },
    {
      title: '登録日',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format(`YYYY-MM-DD`),
      width: 150,
      sorter: (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
      sortDirections: ['descend', 'ascend']
    },
    {
      title: '更新日',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => dayjs(text).format(`YYYY-MM-DD`),
      width: 150,
      sorter: (a, b) => dayjs(a.updated_at).valueOf() - dayjs(b.updated_at).valueOf(),
      sortDirections: ['descend', 'ascend']
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'operation',
      render: (_: any, record: DeviceResponseType) => (
        <Space size='middle'>
          <Button variant='outlined' color='primary' onClick={() => onClickManage(record.id)}>編集</Button>
          <Button variant='outlined' color='danger' onClick={() => {
            Modal.warning({
              title: `${record.name}を削除します`,
              content: 'この操作は元に戻せません。本当に実行しますか？',
              onOk() { onClickDelete(record.id); },
              okCancel: true,
              centered: true,
            });
          }}>
            削除
          </Button>
        </Space>
      ),
      width: 145,
      fixed: 'right'
    }
  ];

  return (
    <div>
      <Button variant="text" color="primary" icon={<LeftOutlined />} onClick={() => navigate(-1)} className="mb-2 -ml-3">戻る</Button>
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">デバイス一覧画面</p>
      </div>
      <Divider />
      <div className="flex p-2 text-xl mb-3">
        <ProfileOutlined className="text-[#1677ff]" />
        <span className="pl-2 break-all">{company?.company_name}</span>
      </div>
      <Flex vertical justify="center" className="relative">
        <Table<DeviceResponseType>
          dataSource={deviceList}
          scroll={{ x: 'max-content' }}
          columns={columns}
          bordered
          pagination={{
            current: currentPage,
            pageSize: currentPageSize,
            total: devicePagination.total,
            position: ['bottomCenter'],
            showSizeChanger: false,
          }}
          onChange={handleTableChange}
          rowKey={(record) => record.id}
        />
      </Flex>
      <Flex gap='small' className="justify-end">
        <Button type="primary" onClick={() => onClickManage('register')}>デバイス新規登録</Button>
      </Flex>
    </div>
  );
}